import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Loader2, RotateCcw, Send, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { currentAssistantIdentity, useAssistant, useAssistantStatus } from "@/hooks/use-assistant";
import { AssistantMessageView } from "./AssistantMessages";

/** Shareable links (kingwashkw.com/assistant, /ai) that open the home page with the chat already open. */
export const ASSISTANT_LINK_PATHS = ["/assistant", "/ai"];

/**
 * Floating AI assistant. The button sits above the WhatsApp button; the chat
 * is full screen on phones and a floating panel on larger screens. Hidden on
 * /booking (like WhatsApp) and when the server has the assistant switched off.
 */
export function AssistantWidget() {
  const { t } = useTranslation();
  const [location, navigate] = useLocation();
  const { data: status } = useAssistantStatus();
  const [open, setOpen] = useState(false);
  const [draftText, setDraftText] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Re-read on every navigation: login and logout both navigate.
  const identity = currentAssistantIdentity();
  const loggedIn = identity !== "guest";
  const chat = useAssistant(identity);

  // Arriving on a shareable link (also after login, which returns to it) opens the chat.
  const onLink = ASSISTANT_LINK_PATHS.includes(location);
  useEffect(() => {
    if (onLink) setOpen(true);
  }, [onLink]);

  const close = useCallback(() => {
    setOpen(false);
    // Leave the link's path, so a reload or Back doesn't open the chat again.
    if (onLink) navigate("/", { replace: true });
  }, [onLink, navigate]);

  useEffect(() => {
    if (!open) return;
    chat.loadHistory();
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, chat.loadHistory, close]);

  // Follow the newest message.
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [chat.messages.length, chat.sending, open]);

  if (!status?.enabled || location === "/booking") return null;

  const submit = () => {
    if (!draftText.trim() || chat.sending) return;
    chat.send(draftText);
    setDraftText("");
  };

  const goTo = (path: string) => {
    // The chat covers the whole screen on phones; get out of the way.
    if (window.matchMedia("(max-width: 639px)").matches) setOpen(false);
    navigate(path);
  };

  const suggestions = [
    t("assistant.suggestions.services"),
    t("assistant.suggestions.book"),
    loggedIn ? t("assistant.suggestions.myOrder") : t("assistant.suggestions.areas"),
    t("assistant.suggestions.support"),
  ];

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 end-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#1C3A5E] text-white shadow-lg ring-2 ring-primary/60 transition-all duration-300 hover:scale-110"
          aria-label={t("assistant.open")}
          title={t("assistant.open")}
          data-testid="button-assistant-fab"
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-label={t("assistant.title")}
          className="fixed inset-0 z-[60] flex flex-col bg-background animate-in fade-in slide-in-from-bottom-4 duration-200 sm:inset-auto sm:bottom-6 sm:end-6 sm:h-[min(640px,calc(100dvh-3rem))] sm:w-[400px] sm:overflow-hidden sm:rounded-2xl sm:border sm:shadow-2xl"
          data-testid="assistant-panel"
        >
          <header className="flex items-center gap-3 bg-[#1C3A5E] px-4 py-3 text-white">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{t("assistant.title")}</p>
              <p className="truncate text-xs text-white/75">{t("assistant.subtitle")}</p>
            </div>
            {chat.messages.length > 0 && (
              <button
                type="button"
                onClick={chat.reset}
                className="rounded-full p-2 transition hover:bg-white/15"
                aria-label={t("assistant.newChat")}
                title={t("assistant.newChat")}
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={close}
              className="rounded-full p-2 transition hover:bg-white/15"
              aria-label={t("assistant.close")}
              data-testid="button-assistant-close"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite">
            {chat.messages.length === 0 && !chat.loadingHistory && (
              <div className="space-y-4">
                <div className="rounded-2xl rounded-es-md bg-muted px-3.5 py-2.5 text-sm leading-relaxed">
                  {t("assistant.welcome")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => chat.send(s)}
                      className="rounded-full border border-primary/40 px-3 py-1.5 text-xs font-medium text-primary transition hover:bg-primary/10"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {chat.loadingHistory && (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {chat.messages.map((m, i) => (
              <AssistantMessageView
                key={`${m.type}-${m.draftId || ""}-${i}`}
                message={m}
                currentPath={location}
                onNavigate={goTo}
                onConfirmDraft={chat.confirmDraft}
                onCancelDraft={chat.cancelDraft}
                onSendMessage={chat.send}
                active={!chat.messages.slice(i + 1).some((later) => later.role === "user")}
              />
            ))}
            {chat.sending && (
              <div className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground" data-testid="assistant-typing">
                <span className="flex gap-1">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground" />
                </span>
                {t("assistant.typing")}
              </div>
            )}
          </div>

          <div className="border-t bg-background px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
            <div className="flex items-end gap-2">
              <Textarea
                ref={inputRef}
                dir="auto"
                rows={1}
                value={draftText}
                maxLength={1000}
                onChange={(e) => setDraftText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder={t("assistant.placeholder")}
                className="max-h-32 min-h-[44px] resize-none rounded-xl text-base sm:text-sm"
                data-testid="input-assistant-message"
              />
              <Button
                type="button"
                size="icon"
                className="h-11 w-11 shrink-0 rounded-xl"
                disabled={!draftText.trim() || chat.sending}
                onClick={submit}
                aria-label={t("assistant.send")}
                data-testid="button-assistant-send"
              >
                <Send className="h-4 w-4 rtl:-scale-x-100" />
              </Button>
            </div>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">{t("assistant.disclaimer")}</p>
          </div>
        </div>
      )}
    </>
  );
}
